import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import Button from "./Buttons";
import { getAllCountryOptions, getStateOptions } from "../utils/countries";
import { shouldShowRegionPicker, defaultRegionId, regionCountryCodes } from "../utils/regions";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";

// Field-name adapter: useAddressAutocomplete emits organization* keys; form uses vendor* keys
const ORG_TO_VENDOR = {
  organizationAddress1: "vendorAddress1",
  organizationCity:     "vendorCity",
  organizationState:    "vendorState",
  organizationZip:      "vendorPostalCode",
  organizationCountry:  "vendorCountry",
};

const TPRMNewVendorModal = ({ isOpen, onClose, onVendorCreated, dealId = null }) => {
  const apiCall = useApi();
  const regions = useRegions();
  const { verticals } = useVerticals();


  const [loading, setLoading] = useState(false);
  const [showClassification, setShowClassification] = useState(false);

  const org = JSON.parse(localStorage.getItem("selectedOrganization"));

  const [form, setForm] = useState({
    vendorName: "",
    organizationId: org?.organizationId,
    vendorOwner: "",
    vendorDomain: "",
    vendorDescription: "",
    vendorAddress1: "",
    vendorAddress2: "",
    vendorCity: "",
    vendorState: "",
    vendorPostalCode: "",
    vendorCountry: "",
    vendorContactNo1: "",
    vendorContactNo2: "",
    tenantId: "1",
    regionId: org?.regionId?.toString() || "",
    verticalId: "",
    subVerticalId: "",
    dataSensitivity: "",
    accessScope: "",
    revenueDependency: "",
  });

  // ── Google Maps / address autocomplete ─────────────────────────────────
  const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } = useGoogleMapsLoader();

  // Keep regionId on a configured region: the org's region when configured, the only region
  // when there is one (the picker is then hidden), otherwise the user's choice.
  useEffect(() => {
    // Wait for the regions to load: an empty list would clear the org's prefilled region.
    if (!regions?.length) return;
    const id = defaultRegionId(regions, form.regionId);
    if (id !== form.regionId) {
      setForm(prev => ({ ...prev, regionId: id }));
    }
  }, [regions, form.regionId]);

  const showRegionPicker = shouldShowRegionPicker(regions);
  const selectedRegion = regions.find(r => String(r.regionId) === String(form.regionId));

  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const regionCodes = useMemo(
    () => regionCountryCodes(selectedRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion?.countryFilterKey]
  );

  const countryOptions = getAllCountryOptions();

  const handleBatchChange = useCallback((updates) => {
    setForm(prev => {
      const mapped = {};
      for (const [key, val] of Object.entries(updates)) {
        mapped[ORG_TO_VENDOR[key] ?? key] = val;
      }
      return { ...prev, ...mapped };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { suggestions, showSuggestions, setShowSuggestions, handleSuggestionSelect, zipLoading } =
    useAddressAutocomplete({
      isLoaded:      googleIsLoaded,
      addressValue:  form.vendorAddress1,
      country:       form.vendorCountry,
      regionCodes,
      zip:           form.vendorPostalCode,
      city:          form.vendorCity,
      state:         form.vendorState,
      onBatchChange: handleBatchChange,
    });

  const addressWrapperRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [setShowSuggestions]);

  const handleCountryChange = (e) => {
    setForm(prev => ({ ...prev, vendorCountry: e.target.value, vendorState: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "verticalId") {
      // Reset SubVertical if Vertical changes
      setForm((prev) => ({ ...prev, verticalId: value, subVerticalId: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    try {
      const payload = {
        vendorName: form.vendorName,
        organizationId: org?.organizationId,
        vendorAddress1: form.vendorAddress1,
        vendorAddress2: form.vendorAddress2,
        vendorCity: form.vendorCity,
        vendorState: form.vendorState,
        vendorPostalCode: form.vendorPostalCode,
        vendorCountry: form.vendorCountry,
        vendorOwner: form.vendorOwner,
        vendorDomain: form.vendorDomain,
        vendorDescription: form.vendorDescription,
        vendorContactNo1: form.vendorContactNo1,
        vendorContactNo2: form.vendorContactNo2,
        tenantId: form.tenantId,
        regionId: form.regionId ? Number(form.regionId) : null,
        verticalId: form.verticalId ? Number(form.verticalId) : null,
        subVerticalId: form.subVerticalId ? Number(form.subVerticalId) : null,
        dataSensitivity: form.dataSensitivity || null,
        accessScope: form.accessScope || null,
        revenueDependency: form.revenueDependency || null,
      };

      const res = await apiCall("POST", "/TPRMVendor", payload);
      onVendorCreated?.(res);
      onClose();

      setForm({
        vendorName: "",
        organizationId: org?.organizationId,
        vendorOwner: "",
        vendorDomain: "",
        vendorDescription: "",
        vendorAddress1: "",
        vendorAddress2: "",
        vendorCity: "",
        vendorState: "",
        vendorPostalCode: "",
        vendorCountry: "",
        vendorContactNo1: "",
        vendorContactNo2: "",
        tenantId: "",
        regionId: org?.regionId?.toString() || "",
        verticalId: "",
        subVerticalId: "",
        dataSensitivity: "",
        accessScope: "",
        revenueDependency: "",
      });
    } catch (err) {
      alert("Failed to create vendor. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !org?.organizationId) return null;

  // Get filtered subVerticals for selected vertical
  const selectedVertical = verticals.find(
    (v) => v.verticalId === parseInt(form.verticalId)
  );
  const subVerticals = selectedVertical?.subVerticals || [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50">
      <div className="bg-theme-secondary p-6 rounded-lg w-[520px] shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-content-primary">
            Create New Vendor
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">

          <Input
            label="Vendor Name"
            name="vendorName"
            value={form.vendorName}
            onChange={handleChange}
            required
          />
          {/* Region Dropdown (hidden when the installation has one region) */}
          {showRegionPicker && (
            <Dropdown
              label="Data Residency Region"
              name="regionId"
              value={form.regionId}
              onChange={handleChange}
              options={regions}
              valueKey="regionId"
              labelKey="regionName"
              placeholder="-- Select Region --"
            />
          )}

          {/* Vertical Dropdown */}
          <Dropdown
            label="Vertical"
            name="verticalId"
            value={form.verticalId}
            onChange={handleChange}
            options={verticals}
            valueKey="verticalId"
            labelKey="verticalName"
            placeholder="-- Select Vertical --"
          />

          {/* SubVertical Dropdown */}
          <Dropdown
            label="Sub-Vertical"
            name="subVerticalId"
            value={form.subVerticalId}
            onChange={handleChange}
            options={subVerticals}
            valueKey="subVerticalId"
            labelKey="subVerticalName"
            placeholder="-- Select Sub-Vertical --"
            disabled={!form.verticalId}
          />
          <Input
            label="Vendor Owner"
            name="vendorOwner"
            value={form.vendorOwner}
            onChange={handleChange}
            required
          />
          <Input
            label="Vendor Domain"
            name="vendorDomain"
            value={form.vendorDomain}
            onChange={handleChange}
            required
          />

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">Country</label>
            <select
              value={form.vendorCountry}
              onChange={handleCountryChange}
              className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
            >
              {countryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Address with Google Places autocomplete */}
          <div ref={addressWrapperRef} className="relative">
            <Input
              label="Address"
              name="vendorAddress1"
              value={form.vendorAddress1}
              onChange={(e) => setForm(prev => ({ ...prev, vendorAddress1: e.target.value }))}
              autoComplete="off"
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder={googleLoadError || googleMapsInactive ? "Enter street address" : "Start typing to search address…"}
            />

            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.10)] overflow-hidden"
                style={{ top: "calc(100% - 1rem)", marginTop: "2px" }}
              >
                {suggestions.map((s, i) => (
                  <li
                    key={s.placePrediction?.placeId || i}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSuggestionSelect(s)}
                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {s.placePrediction?.mainText?.text}
                      </p>
                      {s.placePrediction?.secondaryText?.text && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {s.placePrediction.secondaryText.text}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {googleLoadError && (
              <p className="text-xs text-gray-400 mt-1">
                Address autocomplete unavailable — please enter manually.
              </p>
            )}
          </div>

          {/* Postal Code | State / Province | City */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Input
                label="Postal Code"
                name="vendorPostalCode"
                value={form.vendorPostalCode}
                onChange={(e) => setForm(prev => ({ ...prev, vendorPostalCode: e.target.value }))}
              />
              {zipLoading && (
                <p className="text-xs text-gray-400 mt-1 animate-pulse">Looking up…</p>
              )}
            </div>

            {form.vendorCountry === "US" ? (
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">State / Province</label>
                <select
                  value={form.vendorState}
                  onChange={(e) => setForm(prev => ({ ...prev, vendorState: e.target.value }))}
                  className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                >
                  {getStateOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ) : (
              <Input
                label="State / Province"
                name="vendorState"
                value={form.vendorState}
                onChange={(e) => setForm(prev => ({ ...prev, vendorState: e.target.value }))}
              />
            )}

            <Input
              label="City"
              name="vendorCity"
              value={form.vendorCity}
              onChange={(e) => setForm(prev => ({ ...prev, vendorCity: e.target.value }))}
            />
          </div>

          <Input
            label="Address Line 2"
            name="vendorAddress2"
            value={form.vendorAddress2}
            onChange={handleChange}
          />
          <Input
            label="Contact No 1"
            name="vendorContactNo1"
            value={form.vendorContactNo1}
            onChange={handleChange}
          />
          <Input
            label="Contact No 2"
            name="vendorContactNo2"
            value={form.vendorContactNo2}
            onChange={handleChange}
          />
          <Input
            label="Description"
            name="vendorDescription"
            value={form.vendorDescription}
            onChange={handleChange}
            required
          />




          {/* Vendor Classification (collapsible) */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowClassification(prev => !prev)}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-sm font-medium text-content-secondary hover:bg-gray-100 transition-colors"
            >
              <span>Vendor Classification (Optional)</span>
              {showClassification ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {showClassification && (
              <div className="p-3 space-y-3">
                <Dropdown
                  label="Data Sensitivity"
                  name="dataSensitivity"
                  value={form.dataSensitivity}
                  onChange={handleChange}
                  options={[
                    { value: "PHI", label: "PHI (Protected Health Information)" },
                    { value: "PII", label: "PII (Personal Identifiable Information)" },
                    { value: "Financial", label: "Financial Data" },
                    { value: "Public", label: "Public Data" },
                    { value: "None", label: "None" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  placeholder="-- Select Data Sensitivity --"
                />
                <Dropdown
                  label="Access Scope"
                  name="accessScope"
                  value={form.accessScope}
                  onChange={handleChange}
                  options={[
                    { value: "NetworkAccess", label: "Network Access" },
                    { value: "DataProcessor", label: "Data Processor" },
                    { value: "SaaSOnly", label: "SaaS Only" },
                    { value: "Physical", label: "Physical Access" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  placeholder="-- Select Access Scope --"
                />
                <Dropdown
                  label="Revenue Dependency"
                  name="revenueDependency"
                  value={form.revenueDependency}
                  onChange={handleChange}
                  options={[
                    { value: "Critical", label: "Critical" },
                    { value: "Important", label: "Important" },
                    { value: "Low", label: "Low" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  placeholder="-- Select Revenue Dependency --"
                />
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 mt-5">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create vendor"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

// Reusable Input
const Input = ({ label, type = "text", ...props }) => (
  <div>
    <label className="block text-sm font-medium text-content-secondary mb-1">{label}{props.required && <span className="text-red-500 ml-1">*</span>}</label>
    <input
      type={type}
      {...props}
      className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
    />
  </div>
);

// Reusable Dropdown
const Dropdown = ({
  label,
  name,
  value,
  onChange,
  options,
  valueKey,
  labelKey,
  placeholder,
  disabled = false,
}) => (
  <div>
    <label className="block text-sm font-medium text-content-secondary mb-1">{label}</label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan disabled:opacity-50"
    >
      <option value="">{placeholder}</option>
      {options?.map((opt) => (
        <option key={opt[valueKey]} value={opt[valueKey]}>
          {opt[labelKey]}
        </option>
      ))}
    </select>
  </div>
);

export default TPRMNewVendorModal;
